import { useEffect, useRef, useState } from 'react';

/**
 * Defers mounting heavy homepage blocks (framer-motion / recharts / chat) so they
 * DON'T download during the first-paint window. Mounts when ANY of these happen:
 *   1. the sentinel ref scrolls within `rootMargin` of the viewport, or
 *   2. the visitor interacts (scroll / tap / key) — only when `onInteraction`, or
 *   3. an idle fallback timer fires (keeps content present for crawlers / no-scroll).
 * A URL hash forces an immediate mount so deep-links still work.
 */
export function useDeferredMount({ idleMs = 2500, onInteraction = false, rootMargin = '600px' } = {}) {
  const ref = useRef(null);
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (typeof window !== 'undefined' && window.location.hash) { setShow(true); return undefined; }
    let done = false;
    let io;
    let toId;
    const onInteract = () => trigger();

    const cleanup = () => {
      if (io) io.disconnect();
      window.removeEventListener('pointerdown', onInteract);
      window.removeEventListener('keydown', onInteract);
      window.removeEventListener('scroll', onInteract);
      if (toId) clearTimeout(toId);
    };

    function trigger() {
      if (done) return;
      done = true;
      setShow(true);
      cleanup();
    }

    if (ref.current && 'IntersectionObserver' in window) {
      io = new IntersectionObserver((entries) => {
        if (entries.some((e) => e.isIntersecting)) trigger();
      }, { rootMargin });
      io.observe(ref.current);
    }

    if (onInteraction) {
      window.addEventListener('pointerdown', onInteract, { passive: true });
      window.addEventListener('keydown', onInteract);
      window.addEventListener('scroll', onInteract, { passive: true });
    }

    toId = setTimeout(trigger, idleMs);
    return cleanup;
  }, [idleMs, onInteraction, rootMargin]);

  return [ref, show];
}
